import { ApiError } from "@/api";

/**
 * What a SAMS refusal means for the operator (CATUX U0): the problem, what it affects and what to do next,
 * by the server's code. The server's own message (English) is never the sentence on screen; it stays
 * inside [자세히] with the code. A code not listed here reads as a general sentence, never as raw text.
 */
export type ErrorEntry = { problem: string; impact: string; next: string };

export const ERROR_TEXTS: Record<string, ErrorEntry> = {
  // connection, setup and access
  MODEL_CATALOG_NOT_CONFIGURED: { problem: "이 서버에는 모델 카탈로그가 설정되어 있지 않습니다.",
    impact: "모델·가격을 읽거나 저장할 수 없습니다.", next: "서버 설정(카탈로그 DB 연결)을 관리자에게 확인해 주세요." },
  MODEL_CATALOG_SERVICE_MISSING: { problem: "모델 카탈로그 저장 기능이 이 서버에 설치되어 있지 않습니다.",
    impact: "저장할 수 없습니다. 읽기는 됩니다.", next: "서버 배포 상태를 관리자에게 확인해 주세요." },
  MODEL_PROJECTION_NOT_INSTALLED: { problem: "공유 모델 목록을 갱신하는 DB 함수가 설치되어 있지 않습니다.",
    impact: "저장이 취소되었습니다. 아무것도 저장되지 않았습니다.", next: "DB 설치 상태를 관리자에게 확인해 주세요." },
  CATALOG_UNAVAILABLE: { problem: "카탈로그 DB에 연결하지 못했습니다.", impact: "아무것도 바뀌지 않았습니다.",
    next: "잠시 뒤 다시 시도해 주세요." },
  CATALOG_TENANT: { problem: "이 로그인의 카탈로그 범위가 맞지 않습니다.", impact: "요청을 처리하지 않았습니다.",
    next: "관리자에게 확인해 주세요." },
  PROD_ACCESS_REQUIRED: { problem: "prod 권한이 필요한 작업입니다.", impact: "prod에는 아무것도 읽거나 쓰지 않았습니다.",
    next: "prod 권한이 있는 관리자에게 부탁해 주세요." },
  TARGET_NOT_CONFIGURED: { problem: "이 환경의 Base 연결이 설정되어 있지 않습니다.", impact: "Base를 읽거나 쓰지 않았습니다.",
    next: "서버의 Base 연결 설정을 관리자에게 확인해 주세요." },
  TARGET_UNAVAILABLE: { problem: "Base DB에 연결하거나 읽지 못했습니다.", impact: "Base에는 아무것도 쓰지 않았습니다.",
    next: "잠시 뒤 다시 시도해 주세요. 계속되면 [연결 진단]을 보세요." },
  READ_PERMISSION_MISSING: { problem: "Base DB를 읽을 권한이 없습니다.",
    impact: "이 정보는 확인할 수 없습니다(없는 것이 아닙니다).", next: "Base DB 읽기 권한을 관리자에게 요청해 주세요." },
  TARGET_SCHEMA_MISMATCH: { problem: "Base DB의 표 구조가 카탈로그가 아는 구조와 다릅니다.",
    impact: "Base를 읽거나 쓰지 않았습니다.", next: "Base 마이그레이션 상태를 관리자에게 확인해 주세요." },
  TARGET_SCHEMA_NEWER: { problem: "Base DB가 새 구조(v2)라 SAMS가 아직 쓸 수 없습니다.",
    impact: "Base에는 아무것도 쓰지 않았습니다. 읽기는 됩니다.", next: "v2 쓰기 지원이 들어올 때까지 기다려 주세요." },
  SAMS_AUDIT_UNAVAILABLE: { problem: "변경 기록(감사 로그)을 남기지 못했습니다.",
    impact: "기록 없이 보내지 않으려고 아무것도 보내지 않았습니다.", next: "잠시 뒤 다시 시도해 주세요." },
  REQUEST_TIMEOUT: { problem: "서버가 제때 답하지 않았습니다.",
    impact: "처리됐는지 알 수 없습니다. 같은 요청을 바로 다시 보내지 마세요.", next: "새로고침해서 상태를 먼저 확인해 주세요." },
  BASE_DIRECT_DISABLED: { problem: "SAMS 로그인으로는 Base API를 직접 부르지 않습니다.", impact: "요청을 보내지 않았습니다.",
    next: "SAMS를 거치는 화면 기능을 써 주세요." },
  SAMS_TOKEN_OTHER_HOST: { problem: "SAMS 로그인 토큰을 다른 주소로 보내려 했습니다.", impact: "요청을 보내지 않았습니다.",
    next: "화면 설정(서버 주소)을 관리자에게 확인해 주세요." },
  // deliveries to Base
  TARGET_DRIFT: { problem: "Base 행이 SAMS 기록과 다릅니다. SAMS 밖에서 바뀐 것으로 보입니다.",
    impact: "덮어쓰지 않았습니다.", next: "Base 현재 값을 보고, 덮어써도 될 때만 다시 보내 주세요." },
  DELIVERY_REFUSED: { problem: "Base가 이 변경을 거부했습니다.", impact: "아무것도 적용되지 않았습니다.",
    next: "[자세히]의 이유를 보고 내용을 고쳐 주세요." },
  DELIVERY_NOT_CONFIRMED: { problem: "Base의 답을 확인하지 못했습니다.", impact: "적용됐는지 알 수 없습니다.",
    next: "다시 보내지 말고 [확인]으로 Base 실제 행과 대조해 주세요." },
  DELIVERY_UNSETTLED: { problem: "결과를 모르는 이전 배포가 있습니다.", impact: "새로 보내지 않았습니다.",
    next: "[확인]으로 이전 배포를 먼저 대조해 주세요." },
  DEPLOYMENT_IN_PROGRESS: { problem: "같은 항목의 배포가 이미 진행 중입니다.", impact: "새로 보내지 않았습니다.",
    next: "끝날 때까지 기다린 뒤 새로고침해 주세요." },
  DEV_NOT_SERVING: { problem: "dev에 SAMS로 적용한 기록이 없거나, dev 실제 행이 계획과 다릅니다.",
    impact: "prod에는 보내지 않았습니다.", next: "dev에 먼저 보내고 결과를 확인해 주세요." },
  NOTHING_TO_RECONCILE: { problem: "대조할 미확인 배포가 없습니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  NOT_NEEDS_OPERATOR: { problem: "사람 판단이 필요한 배포가 아닙니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  OPERATOR_HASH_CHANGED: { problem: "그사이 Base 행이 바뀌었습니다.", impact: "확정하지 않았습니다.",
    next: "지금 행을 다시 보고 확정해 주세요." },
  SETTLE_ROW_ABSENT: { problem: "Base에 행이 없어 확정할 수 없습니다.", impact: "확정하지 않았습니다.", next: "[확인]으로 대조해 주세요." },
  SETTLE_DIGEST_REQUIRED: { problem: "확정할 행의 해시가 빠졌습니다.", impact: "확정하지 않았습니다.", next: "창을 닫고 다시 열어 주세요." },
  SETTLE_DIGEST_MISMATCH: { problem: "확정하려는 행이 본 행과 다릅니다.", impact: "확정하지 않았습니다.", next: "다시 열어 지금 행을 확인해 주세요." },
  OPERATOR_SETTLEMENT_REFUSED: { problem: "카탈로그가 이 확정을 거부했습니다.", impact: "확정하지 않았습니다.",
    next: "[자세히]의 코드를 관리자에게 알려 주세요." },
  IDENTITY_CHANGED: { problem: "Base 모델의 연결 방식·API 모델·ID는 바꿀 수 없습니다.", impact: "보내지 않았습니다.",
    next: "새 모델로 만들고 옛 모델을 은퇴시켜 주세요." },
  NOT_STAGED: { problem: "Base에 이 버전이 숨김 상태로 없습니다.", impact: "공개하지 않았습니다.", next: "모델을 먼저 보내 주세요." },
  PRICE_NOT_IN_EFFECT: { problem: "Base에 시행 중인 가격이 없습니다.", impact: "공개하지 않았습니다.",
    next: "가격을 보내고 시행 시각이 지난 뒤 공개해 주세요." },
  PRICE_NOT_LIVE: { problem: "기준선이거나 철회된 가격이라 보내지 않습니다.", impact: "보내지 않았습니다.", next: "SAMS에서 만든 새 가격을 보내 주세요." },
  PRICE_DEADLINE_PASSED: { problem: "시행 시각이 너무 가까워 제때 Base에 넣을 수 없습니다.", impact: "보내지 않았습니다.",
    next: "더 늦은 시행 시각으로 새 가격을 만들어 주세요." },
  PRICE_KEY_TAKEN: { problem: "Base에 같은 시행 시각의 다른 가격이 이미 있습니다.", impact: "보내지 않았습니다.",
    next: "다른 시행 시각을 골라 주세요." },
  PRICE_NOT_FUTURE: { problem: "시행 시각은 지금 이후여야 합니다.", impact: "저장하지 않았습니다.", next: "시행 시각을 고쳐 주세요." },
  REQUIREMENTS_STALE: { problem: "Base가 어떤 모델을 쓰는지 최근에 확인하지 못했습니다.", impact: "모델을 끄지 않았습니다.",
    next: "요구 모델 관측이 새로 된 뒤 다시 시도해 주세요." },
  MODEL_REQUIRED: { problem: "Base가 이 모델을 쓰고 있습니다.", impact: "모델을 끄지 않았습니다.",
    next: "쓰는 곳을 먼저 다른 모델로 바꿔 주세요." },
  ACTOR_REQUIRED: { problem: "누가 요청했는지 알 수 없습니다.", impact: "처리하지 않았습니다.", next: "다시 로그인해 주세요." },
  // saves
  VERSION_CONFLICT: { problem: "그사이 다른 버전이 저장되었습니다.", impact: "저장하지 않았습니다.",
    next: "새로고침해서 최신 버전에서 다시 고쳐 주세요." },
  PREVIEW_CHANGED: { problem: "미리 본 뒤에 내용이 바뀌었습니다.", impact: "저장하지 않았습니다.", next: "다시 미리 보고 저장해 주세요." },
  NOT_ACTIVE: { problem: "저장은 됐지만 쓰는 버전이 아닙니다.", impact: "화면의 쓰는 버전은 그대로입니다.", next: "새로고침해 주세요." },
  NOT_FOUND: { problem: "찾는 항목이 없습니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  MODEL_NOT_FOUND: { problem: "그 모델이 카탈로그에 없습니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  PRICING_NOT_FOUND: { problem: "그 가격이 카탈로그에 없습니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  MODEL_EXISTS: { problem: "같은 ID의 모델이 이미 있습니다.", impact: "새로 만들지 않았습니다.", next: "기존 모델을 편집해 주세요." },
  MODEL_UNKNOWN: { problem: "카탈로그에 없는 모델입니다.", impact: "저장하지 않았습니다.", next: "모델을 먼저 등록해 주세요." },
  MODEL_UPDATE_INVALID: { problem: "모델 값이 규칙에 맞지 않습니다.", impact: "저장하지 않았습니다.", next: "[자세히]의 칸을 고쳐 주세요." },
  INVALID_INPUT: { problem: "입력값이 규칙에 맞지 않습니다.", impact: "저장하지 않았습니다.", next: "[자세히]의 칸을 고쳐 주세요." },
  BAD_REQUEST: { problem: "요청 형식이 맞지 않습니다.", impact: "처리하지 않았습니다.", next: "새로고침한 뒤 다시 시도해 주세요." },
  SOURCE_REQUIRED: { problem: "값의 출처가 필요합니다.", impact: "저장하지 않았습니다.", next: "어디서 본 값인지 출처를 적어 주세요." },
  BASELINE_INVALID: { problem: "기준선 파일 형식이 맞지 않습니다.", impact: "가져오지 않았습니다.", next: "기준선을 다시 만들어 주세요." },
  // call sites
  CALL_SITE_INVALID: { problem: "호출 지점 설정이 규칙에 맞지 않습니다.", impact: "저장하지 않았습니다.", next: "[자세히]의 문제를 고쳐 주세요." },
  CALL_SITES_NOT_INSTALLED: { problem: "호출 지점 표가 이 서버 DB에 없습니다.", impact: "호출 지점을 읽거나 저장할 수 없습니다.",
    next: "DB 설치 상태를 관리자에게 확인해 주세요." },
  SITE_UNKNOWN: { problem: "모르는 호출 지점입니다.", impact: "저장하지 않았습니다.", next: "새로고침해 주세요." },
  GROUP_UNKNOWN: { problem: "모르는 호출 지점 묶음입니다.", impact: "저장하지 않았습니다.", next: "새로고침해 주세요." },
  PROMPT_UNKNOWN: { problem: "모르는 프롬프트입니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  MODEL_PIN_INVALID: { problem: "지정한 모델 버전을 쓸 수 없습니다.", impact: "저장하지 않았습니다.", next: "모델을 다시 골라 주세요." },
  PIN_CONFLICT: { problem: "모델 버전 지정이 서로 맞지 않습니다.", impact: "저장하지 않았습니다.", next: "새로고침해서 다시 골라 주세요." },
  INHERIT_TARGET_REMOVED: { problem: "따르던 지점이 없어졌습니다.", impact: "저장하지 않았습니다.", next: "다른 방식을 골라 주세요." },
  SCORING_CONFIRMATION_REQUIRED: { problem: "채점 지점은 바꾸기 전에 확인과 사유가 필요합니다.", impact: "저장하지 않았습니다.",
    next: "'점수 비교 불가'를 확인하고 사유를 적어 주세요." },
  BUNDLE_CONFLICT: { problem: "그사이 다른 배정 묶음이 게시되었습니다.", impact: "저장하지 않았습니다.", next: "새로고침해서 다시 저장해 주세요." },
  MANIFEST_MISSING: { problem: "호출 지점 목록(manifest)이 없습니다.", impact: "저장하지 않았습니다.", next: "서버 배포 상태를 관리자에게 확인해 주세요." },
  MANIFEST_MISMATCH: { problem: "호출 지점 목록이 서버 코드와 다릅니다.", impact: "저장하지 않았습니다.", next: "서버 배포 상태를 관리자에게 확인해 주세요." },
  BASE_MANIFEST_NOT_INSTALLED: { problem: "Base 호출 지점 목록이 설치되어 있지 않습니다.", impact: "Base 지점을 읽을 수 없습니다.",
    next: "관리자에게 확인해 주세요." },
  BASE_ISOLATION: { problem: "Base 배정을 안전하게 읽을 수 없습니다.", impact: "읽지 않았습니다.", next: "관리자에게 확인해 주세요." },
  BASE_BUNDLE_INVALID: { problem: "Base 배정 묶음이 규칙에 맞지 않습니다.", impact: "쓰지 않았습니다.", next: "[자세히]를 관리자에게 알려 주세요." },
  DELIVERY_NOT_FOUND: { problem: "그 전달 기록이 없습니다.", impact: "아무것도 바뀌지 않았습니다.", next: "새로고침해 주세요." },
  // OpenRouter lookups (the saved values are never touched by one)
  OPENROUTER_NOT_FOUND: { problem: "OpenRouter에 같은 이름의 모델이 없습니다.", impact: "기존 값은 그대로입니다.",
    next: "이름을 확인하거나 직접 입력해 주세요." },
  OPENROUTER_RATE_LIMITED: { problem: "OpenRouter 조회 한도에 걸렸습니다.", impact: "기존 값은 그대로입니다.", next: "잠시 뒤 다시 조회해 주세요." },
  OPENROUTER_TIMEOUT: { problem: "OpenRouter가 제때 답하지 않았습니다.", impact: "기존 값은 그대로입니다.", next: "잠시 뒤 다시 조회해 주세요." },
  OPENROUTER_SHAPE_CHANGED: { problem: "OpenRouter 응답 형식이 바뀌어 읽지 못했습니다.", impact: "자동값을 쓰지 않습니다.",
    next: "값을 직접 입력해 주세요." },
};

const GENERAL: ErrorEntry = { problem: "요청을 처리하지 못했습니다.", impact: "무엇이 바뀌었는지 이 화면에서는 알 수 없습니다.",
  next: "새로고침해서 상태를 확인하고, 계속되면 [자세히]의 코드를 관리자에게 알려 주세요." };

export type ExplainedError = ErrorEntry & {
  /** The code the entry was found by (after reading the details), or the server's own code. */
  code: string;
  known: boolean;
  status: number | null;
  /** The server's message and code, for [자세히] only. */
  raw: string;
};

/** Whether a TARGET_SCHEMA_MISMATCH is in fact a missing SELECT (information_schema hides such a table's columns). */
export function readPermissionMissing(detail: Record<string, unknown> | undefined) {
  const privileges = detail?.privileges as Record<string, { can_select?: boolean | null } | null> | null | undefined;
  if (!privileges) return [];
  return Object.entries(privileges).filter(([, p]) => p && p.can_select === false).map(([table]) => table);
}

export function explainError(err: unknown): ExplainedError {
  if (err instanceof ApiError) {
    let code = err.code;
    if (code === "TARGET_SCHEMA_MISMATCH" && readPermissionMissing(err.detail).length) code = "READ_PERMISSION_MISSING";
    const entry = ERROR_TEXTS[code];
    const raw = [err.message, err.code && `(${err.code})`, err.status ? `HTTP ${err.status}` : ""].filter(Boolean).join(" ");
    return { ...(entry ?? GENERAL), code: code || err.code, known: Boolean(entry), status: err.status || null, raw };
  }
  const raw = err instanceof Error ? err.message : String(err);
  // A fetch that never reached the server (offline, CORS, DNS): the browser's TypeError.
  if (err instanceof TypeError) {
    return { problem: "서버에 연결하지 못했습니다.", impact: "요청이 서버에 닿지 않았습니다.",
      next: "네트워크를 확인하고 다시 시도해 주세요.", code: "NETWORK", known: true, status: null, raw };
  }
  return { ...GENERAL, code: "", known: false, status: null, raw };
}

/** One sentence for a place that shows text only: the problem and the next step, in Korean, never the raw message. */
export function errorText(err: unknown) {
  const e = explainError(err);
  return e.known ? `${e.problem} ${e.next}` : `${e.problem} ${e.code ? `(오류 코드 ${e.code}) ` : ""}${e.next}`;
}
